import { useRef, useState, type DragEvent, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, FileText, Upload } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { saveBlob } from "../../lib/download";
import { DialogRow } from "./DialogRow";
import { formatCount, MAX_CSV_BYTES, plural, refusedCsv } from "./roster";
import type {
  RosterImportGroup,
  RosterImportMode,
  RosterRefusedLine,
} from "./types";
import styles from "./table.module.css";

/**
 * Import a table on the Bots page from a CSV — the roster, or the guild
 * templates.
 *
 * ONE dialog for both, because there was never more than one dialog here. The
 * two servers answer in the same vocabulary on purpose (see
 * GuildTemplateImportResult, whose doc says so in as many words), the two
 * tables sit on one page, and an operator meets both in the same afternoon.
 * What actually differs is the noun and the five columns of the preview table,
 * and those are props.
 *
 * Nothing is written until the operator has seen what will arrive: the file is
 * sent once with `preview` and once for real, through the same parse over the
 * same inputs, so the number on the button is the number that lands. The
 * button is dead while a preview is in flight — a mode changed mid-round-trip
 * leaves the OLD count on screen, and importing against a number the operator
 * agreed to for the other mode is exactly what `replace_unused` re-previews to
 * avoid.
 *
 * The refusals are GROUPED, and that is the point of the dialog rather than a
 * detail of it. Five refused lines can be read as a list; three hundred cannot,
 * and «imported 468 of 506» with no breakdown is silence with a number in it.
 * Each group carries its count, its offending values and up to three sample
 * lines, the counts add up to the total by construction, and the whole flat
 * list goes back out as a file the operator fixes and re-imports.
 *
 * Mockups: t-103/bots-source.html §4 and t-103/bots-guild-templates.html §4 —
 * two drawings of one dialog.
 */

/**
 * What both imports answer, preview or not. The two Go types are the same
 * shape field for field; this is that shape, with the one row type that does
 * differ left as a parameter.
 */
export interface ImportCsvResult<Item> {
  /**
   * The header row of the file that was just read, exactly as it was spelled.
   *
   * It is what «Download refused lines» writes above the operator's own lines,
   * because those lines are the file's — in the file's column order, with the
   * file's column count. The canonical header built from this project's schema
   * agrees with neither when the file was written elsewhere, and does not
   * agree at all on a project with an empty `stats_schema`, where the Go
   * export adds `stat:` columns the browser never sees.
   *
   * OPTIONAL while the server is being taught to send it: until then the
   * dialog falls back to the schema header it already draws above the drop
   * zone, which is what it wrote before.
   */
  header?: string;
  added: number;
  /** How many unused rows `replace_unused` cleared. A spent row is never
   *  touched by an import, in either mode. */
  removed: number;
  refused: number;
  groups: RosterImportGroup[];
  preview: Item[];
  refused_lines: RosterRefusedLine[];
}

interface Props<Item, Res extends ImportCsvResult<Item>> {
  title: string;
  /** The noun this file is made of, for every count on the screen. */
  nouns: { one: string; many: string };
  /** The drop zone's own line, before a file is picked. */
  dropTitle: string;
  /** The hidden input's accessible name. */
  fileLabel: string;
  /** This project's own header, built from its schema by the tab that opens
   *  the dialog, so the dialog and the empty state's «Download template»
   *  cannot disagree about what a file looks like. */
  header: string;
  /** The sentence under the mode toggle: what an import never touches, and
   *  why. It is different for the two tables because the reason is. */
  modeNote: ReactNode;
  /** What the refused lines are saved as. */
  refusedFileName: string;
  run: (input: {
    body: string;
    mode: RosterImportMode;
    preview: boolean;
  }) => Promise<Res>;
  /** The endpoint's refusals, as sentences. */
  errorOf: (err: unknown) => string;
  /** The preview table's header row — the half of this dialog that is not the
   *  same for a roster and for a table of guilds. */
  previewHead: ReactNode;
  previewRow: (row: Item, index: number) => ReactNode;
  onClose: () => void;
  /** The real import landed — the tab toasts it and refetches. */
  onDone: (result: Res) => void;
}

export function ImportCsvModal<Item, Res extends ImportCsvResult<Item>>({
  title,
  nouns,
  dropTitle,
  fileLabel,
  header,
  modeNote,
  refusedFileName,
  run,
  errorOf,
  previewHead,
  previewRow,
  onClose,
  onDone,
}: Props<Item, Res>) {
  const [mode, setMode] = useState<RosterImportMode>("append");
  const [file, setFile] = useState<{
    name: string;
    size: number;
    /** Null until the file has been read — an over-large one never is. */
    lines: number | null;
  } | null>(null);
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<Res | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const runPreview = useMutation({
    mutationFn: (input: { body: string; mode: RosterImportMode }) =>
      run({ ...input, preview: true }),
    onSuccess: (res) => {
      setPreview(res);
      // The first group open, the rest folded: the top reason is the one an
      // operator acts on, and four expanded groups is the list again.
      setOpen(res.groups.length > 0 ? [res.groups[0].reason] : []);
      setProblem(null);
    },
    onError: (err) => {
      setPreview(null);
      setProblem(errorOf(err));
    },
  });

  const runImport = useMutation({
    mutationFn: () => run({ body, mode, preview: false }),
    onSuccess: onDone,
    onError: (err) => setProblem(errorOf(err)),
  });

  const busy = runPreview.isPending || runImport.isPending;

  async function load(
    picked: globalThis.File | null | undefined,
    next: RosterImportMode,
  ) {
    if (!picked) return;
    // The file NAMED on screen and the body that would be imported are one
    // fact, and they are cleared together, HERE, before any check can return
    // early. Left to the branches below, a refused file kept the previous
    // file's body armed: the drop zone said B, `changeMode` re-previewed A and
    // wiped the refusal with a green count, and Import wrote A — in
    // `replace_unused`, over the unused half of the table.
    setFile({ name: picked.name, size: picked.size, lines: null });
    setBody("");
    setPreview(null);
    setProblem(null);

    if (picked.size > MAX_CSV_BYTES) {
      setProblem(
        "That file is over 2 MB, which is more than one import can carry. Split it and import the halves.",
      );
      return;
    }
    let text: string;
    try {
      text = await readText(picked);
    } catch {
      setProblem("Couldn't read this file. Try picking it again.");
      return;
    }
    setFile({ name: picked.name, size: picked.size, lines: countLines(text) });
    setBody(text);
    runPreview.mutate({ body: text, mode: next });
  }

  function changeMode(next: RosterImportMode) {
    setMode(next);
    // The preview depends on the mode: `replace_unused` frees the names of the
    // rows it is about to delete, so the same file refuses a different set of
    // lines. Re-reading it is what keeps the count on the button honest — and
    // the button is disabled until that answer lands.
    if (body) runPreview.mutate({ body, mode: next });
  }

  function toggleGroup(reason: string) {
    setOpen((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  }

  function downloadRefused() {
    if (!preview) return;
    // The SOURCE file's header when the server sends it back, and only then
    // this project's canonical one: the rows in this file are the operator's
    // own lines, so a header that describes a different set of columns makes
    // every one of them unreadable on the way back in.
    saveBlob(
      new Blob([refusedCsv(preview.header || header, preview.refused_lines)], {
        type: "text/csv;charset=utf-8",
      }),
      refusedFileName,
    );
  }

  const added = preview?.added ?? 0;
  // The count on the button is the count the operator agreed to. While a
  // preview is in flight the number on screen belongs to the previous answer,
  // so there is nothing here to agree to yet.
  const stale = runPreview.isPending;

  return (
    <Modal
      open
      width="lg"
      title={title}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          {preview && !stale && (
            <span className={styles.footNote}>
              {preview.refused > 0
                ? `Refused lines are skipped; the other ${formatCount(added)} are written.`
                : "Every line of this file can be written."}
            </span>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {/* `!body` as well as `!preview`: a preview already in flight when a
              second file is refused lands afterwards and would otherwise arm
              this button over a body that has just been cleared. */}
          <Button
            onClick={() => runImport.mutate()}
            disabled={!preview || !body || added === 0 || stale}
            loading={runImport.isPending}
          >
            {added === 0 || stale
              ? `Import ${nouns.many}`
              : `Import ${plural(added, nouns.one, nouns.many)}`}
          </Button>
        </>
      }
    >
      <DialogRow label="Mode">
        <SegmentedToggle
          aria-label="Import mode"
          value={mode}
          onChange={changeMode}
          options={[
            { value: "append", label: "Append" },
            { value: "replace_unused", label: "Replace unused rows" },
          ]}
        />
        <div className={styles.modeHelper}>
          {modeNote}
          {mode === "replace_unused" &&
            preview != null &&
            ` ${plural(preview.removed, "unused row is", "unused rows are")} deleted first.`}
        </div>
      </DialogRow>

      <div
        className={
          dragOver ? `${styles.dropZone} ${styles.dropZoneOver}` : styles.dropZone
        }
        onDragOver={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragOver(false);
          void load(e.dataTransfer?.files?.[0], mode);
        }}
      >
        <span aria-hidden="true">
          {file ? <FileText size={18} /> : <Upload size={18} />}
        </span>
        <div className={styles.dropCol}>
          <div className={styles.dropTitle}>{file ? file.name : dropTitle}</div>
          {/* The line count first, because it is the number an operator
              cross-checks the report against: 506 lines is 468 written plus 37
              refused plus the header. The weight alone answers nothing. */}
          <div className={styles.dropSub}>
            {file
              ? [
                  file.lines === null
                    ? null
                    : plural(file.lines, "line", "lines"),
                  `${formatCount(Math.max(1, Math.round(file.size / 1024)))} KB`,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : ".csv · up to 2 MB"}
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {file ? "Choose another file" : "Choose file"}
        </Button>
      </div>

      {/* The columns are this project's own schema, which is the only way a
          first import does not fail on guessing names. */}
      <div className={styles.csvHead}>{header}</div>

      {problem && (
        <Alert variant="danger" title="This file was not read">
          {problem}
        </Alert>
      )}

      {runPreview.isPending && <p className={styles.helper}>Reading the file…</p>}

      {preview && (
        <>
          <div className={styles.previewHead}>
            <span>
              <b>{formatCount(preview.added)}</b>{" "}
              {preview.added === 1
                ? `${nouns.one} will be added`
                : `${nouns.many} will be added`}
            </span>
            {preview.refused > 0 && (
              <span className={styles.previewBad}>
                <b>{formatCount(preview.refused)}</b>{" "}
                {preview.refused === 1 ? "line refused" : "lines refused"}
              </span>
            )}
          </div>

          {preview.groups.length > 0 && (
            <div className={styles.card}>
              {preview.groups.map((g) => {
                const expanded = open.includes(g.reason);
                return (
                  <div key={g.reason} className={styles.errGroup}>
                    <button
                      type="button"
                      className={styles.errRow}
                      aria-expanded={expanded}
                      onClick={() => toggleGroup(g.reason)}
                    >
                      <span className={styles.errCount}>
                        {formatCount(g.count)}
                      </span>
                      <span className={styles.errText}>
                        <b>{g.title}</b>
                        {g.detail && <> — {g.detail}</>}
                      </span>
                      <span className={styles.errChev} aria-hidden="true">
                        {expanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </span>
                    </button>
                    {expanded && (
                      <div className={styles.errBody}>
                        {g.samples.map((s) => (
                          <div key={s.line} className={styles.errLine}>
                            <span className={styles.mono}>line {s.line}</span>
                            <span>{sampleLine(s)}</span>
                          </div>
                        ))}
                        {g.count > g.samples.length && (
                          <div className={styles.errMore}>
                            …and {formatCount(g.count - g.samples.length)} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className={styles.cardFoot}>
                <span>
                  Every refused line is accounted for —{" "}
                  {preview.groups.map((g) => g.count).join(" + ")} ={" "}
                  {formatCount(preview.refused)}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon={<Download size={13} />}
                  onClick={downloadRefused}
                >
                  Download refused lines
                </Button>
              </div>
            </div>
          )}

          <div className={styles.previewHead}>
            <span>First {nouns.many} that will be written</span>
            <span className={styles.mono}>the file is never modified</span>
          </div>
          <div className={styles.card}>
            {preview.preview.length === 0 ? (
              <p className={styles.emptyPreview}>
                Nothing in this file can be written — every line was refused.
              </p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>{previewHead}</tr>
                </thead>
                <tbody>{preview.preview.map(previewRow)}</tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Last in the body so the dialog's initial focus lands on a control the
          operator can see — Modal focuses the first focusable it finds. */}
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv,text/plain"
        className={styles.fileInput}
        aria-label={fileLabel}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          // Reset so picking the same file twice fires change again.
          e.target.value = "";
          void load(picked, mode);
        }}
      />
    </Modal>
  );
}

/**
 * One refused line, said once.
 *
 * Most refusals name the offending VALUE and the row's name is what identifies
 * them — «MashaK — RUS is not two letters». But `name_taken`'s offending value
 * IS the row's name, quoted by the server with `%q`, so putting our own copy in
 * front of it printed «MashaK — "MashaK" is already carried by a player or by
 * another row». Where the message already opens with the name, it is left to
 * do the naming.
 */
function sampleLine(s: RosterRefusedLine): string {
  const name = s.name || "(no name)";
  return s.message.startsWith(JSON.stringify(name))
    ? s.message
    : `${name} — ${s.message}`;
}

/**
 * How many lines this file has, counted the way an operator counts them.
 *
 * It is the number they cross-check the report against — 506 lines is 468
 * written plus 37 refused plus the header — and dropping it left the drop zone
 * saying only how many kilobytes the file weighs, which answers nothing. A
 * trailing newline is not a line: a file ending in one has as many lines as one
 * that does not.
 */
function countLines(text: string): number {
  const body = text.replace(/(\r\n|\r|\n)$/, "");
  if (body === "") return 0;
  return body.split(/\r\n|\r|\n/).length;
}

/** FileReader rather than `Blob.text()`, the same way the other file screens of
 *  this panel read theirs — one idiom, one failure path. */
function readText(file: globalThis.File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}
